import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { IconKonva } from "@project/floorplanner/konva/icon-konva";
import { MonitoringAreaKonva } from "@project/floorplanner/konva/monitoring-area-konva";
import { RemoteSensorKonva, RemoteSensorKonvaShapes } from "@project/floorplanner/konva/remote-sensor-konva";
import { RemoteSensorWireKonva } from "@project/floorplanner/konva/remote-sensor-wire-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { Circle } from "konva/lib/shapes/Circle";

export interface TransmitterKonvaShapes {
  transmitterGroup: Konva.Group;
  transmitter: Konva.Image;
  monitoringArea: Konva.Circle;
}

export class TransmitterKonva {
  static init(
    transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder,
    transmitterIcon: Konva.Image,
    warningSignIcon: Konva.Image,
    productGroup?: Konva.Group,
  ): TransmitterKonvaShapes {
    const group = new Konva.Group({
      id: transmitter.id,
      [FloorplanWorkspaceKonvaAdapter.OBJECT_KEY]: transmitter,
    });

    const monitoringAreaGroup = new Konva.Group();
    group.add(monitoringAreaGroup);

    const monitoringArea = MonitoringAreaKonva.init(transmitter);
    monitoringAreaGroup.add(monitoringArea);

    const transmitterGroup = IconKonva.init(
      transmitter,
      transmitterIcon,
      warningSignIcon,
      productGroup,
      (icon: Konva.Image) => {
        MonitoringAreaKonva.setPosition(monitoringArea, icon.x(), icon.y(), icon.width() * icon.scaleX());
      },
      (icon: Konva.Image) => {
        MonitoringAreaKonva.setPosition(monitoringArea, icon.x(), icon.y(), icon.width() * icon.scaleX());
        MonitoringAreaKonva.setStrokeWidth(monitoringArea, icon.scaleX());
        // Persist the radius so the next load starts with the correct value
        transmitter.monitoringAreaRadius = monitoringArea.radius();
      },
    );
    MonitoringAreaKonva.setPosition(
      monitoringArea,
      transmitterIcon.x(),
      transmitterIcon.y(),
      transmitterIcon.width() * transmitterIcon.scaleX(),
    );
    MonitoringAreaKonva.applyInitialScale(monitoringArea, transmitterIcon.scaleX());
    group.add(transmitterGroup);

    return {
      transmitterGroup: group,
      transmitter: transmitterIcon,
      monitoringArea: monitoringArea,
    };
  }

  static attachRemoteSensor(
    transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder,
    remoteSensorIcon: Konva.Image,
    group: Konva.Group,
  ): RemoteSensorKonvaShapes {
    const transmitterIcon = this.getTransmitterIcon(group);
    const remoteSensorKonvaShapes = RemoteSensorKonva.init(
      transmitter,
      transmitter.remoteSensor!,
      remoteSensorIcon,
      transmitterIcon,
    );
    this.initRemoteSensorWireEventListeners(
      transmitterIcon,
      remoteSensorKonvaShapes.remoteSensor,
      RemoteSensorKonva.getWire(remoteSensorKonvaShapes.group),
    );

    const transmitterMonitoringArea = this.getTransmitterMonitoringArea(group);
    this.getMonitoringAreaGroup(group).add(remoteSensorKonvaShapes.monitoringArea);
    remoteSensorKonvaShapes.monitoringArea.visible(transmitterMonitoringArea.visible());
    transmitterMonitoringArea.visible(false);

    group.add(remoteSensorKonvaShapes.group);
    return remoteSensorKonvaShapes;
  }

  static detachRemoteSensor(group: Konva.Group): void {
    const remoteSensorGroup = this.getRemoteSensorGroup(group);
    const transmitterMonitoringArea = this.getTransmitterMonitoringArea(group);
    if (remoteSensorGroup) {
      const remoteSensorMonitoringArea = this.getRemoteSensorMonitoringArea(group);
      transmitterMonitoringArea.visible(remoteSensorMonitoringArea.visible());
      transmitterMonitoringArea.size(remoteSensorMonitoringArea.size());
      remoteSensorGroup.destroy();
      remoteSensorMonitoringArea.destroy();
    }
  }

  static refreshTransmitterItemText(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder, group: Group) {
    const iconGroup = group.getChildren()[1] as Group;
    IconKonva.refreshItemText(transmitter, iconGroup);
  }

  static setMonitoringAreaVisibility(group: Konva.Group, visible: boolean) {
    let monitoringArea = this.getRemoteSensorMonitoringArea(group);
    if (!monitoringArea) {
      monitoringArea = this.getTransmitterMonitoringArea(group);
    }
    monitoringArea.visible(visible);
  }

  static setMonitoringAreaRadius(transmitterGroup: Konva.Group, radius: number) {
    let monitoringArea = this.getRemoteSensorMonitoringArea(transmitterGroup);
    if (!monitoringArea) {
      monitoringArea = this.getTransmitterMonitoringArea(transmitterGroup);
    }
    monitoringArea.radius(radius);
  }

  static refreshWarningSignVisibility(group: Group, visible: boolean) {
    IconKonva.refreshWarningSignVisibility(this.getTransmitterGroup(group), visible);
  }

  private static initRemoteSensorWireEventListeners(
    transmitterIcon: Konva.Image,
    remoteSensorIcon: Konva.Image,
    wire: Konva.Line,
  ) {
    transmitterIcon.on("dragmove", () => {
      RemoteSensorWireKonva.updateWire(wire, transmitterIcon, remoteSensorIcon);
    });
    transmitterIcon.on("transform", () => {
      RemoteSensorWireKonva.updateWire(wire, transmitterIcon, remoteSensorIcon);
    });
  }

  private static getMonitoringAreaGroup(group: Konva.Group): Konva.Group {
    return group.getChildren()[0] as Konva.Group;
  }

  private static getTransmitterMonitoringArea(group: Konva.Group): Circle {
    return this.getMonitoringAreaGroup(group).getChildren()[0] as Circle;
  }

  private static getRemoteSensorMonitoringArea(group: Konva.Group): Circle {
    return this.getMonitoringAreaGroup(group).getChildren()[1] as Circle;
  }

  private static getTransmitterGroup(group: Konva.Group): Konva.Group {
    return group.getChildren()[1] as Konva.Group;
  }

  private static getRemoteSensorGroup(group: Konva.Group): Konva.Group | undefined {
    return group.getChildren()[2] as Konva.Group;
  }

  private static getTransmitterIcon(group: Konva.Group): Konva.Image {
    const transmitterGroup = this.getTransmitterGroup(group);
    return transmitterGroup.getChildren()[1] as Konva.Image;
  }
}
